/**
 * The `<notations>` element of one `<note>`: everything written on it that is not the note.
 *
 * Ties, slurs, tuplet brackets, ornaments, articulations, fermatas and arpeggio signs all
 * live in this one child, and the format lets them appear in any order. The order below is
 * the order an engraver reads them in -- what joins this note to its neighbours, then what
 * is written above it, then how it is held and spread -- so a file of ours is legible to a
 * person as well as to a parser.
 *
 * A chord is several `<note>` elements here and one event in the document, so most of these
 * marks are written on the first of them only: an articulation repeated on every chord
 * member is drawn several times over. The arpeggio sign is the exception, because it is a
 * sign *about* the chord and has to reach both ends of it.
 */

import type { Arpeggio, Articulation, FermataShape, MusicEvent, Ornament } from "../document/types";
import type { NotePitch } from "../document/pitch";
import { el, leaf } from "../xml/write";
import { technicalNode } from "./export-tab";
import type { XmlNode } from "../xml/write";

/** A slur end landing on this event, with the number that pairs it with its other end. */
export interface SlurMark {
  readonly type: "start" | "stop";
  readonly number: number;
  readonly placement: "above" | "below";
}

/**
 * A glissando end landing on this event.
 *
 * It has no placement: a glissando is drawn between two note heads and there is nowhere
 * else for it to go, so the side of the staff it would be on means nothing.
 */
export interface GlissMark {
  readonly type: "start" | "stop";
  readonly number: number;
}

/** Where this event sits in a tuplet group, when it is in one. */
export type TupletMark = "start" | "stop" | null;

/** What one `<note>` of an event carries, once the chord has been taken into account. */
export interface EventMarks {
  readonly articulations: readonly Articulation[];
  readonly ornaments: readonly Ornament[];
  readonly tremolo: number;
  readonly fermata: FermataShape | null;
  readonly arpeggio: Arpeggio | null;
  /** Which end of the chord this note is, for the bracket a `"bracket"` arpeggio draws. */
  readonly chordEnd: "top" | "bottom" | null;
}

export const NO_MARKS: EventMarks = {
  articulations: [],
  ornaments: [],
  tremolo: 0,
  fermata: null,
  arpeggio: null,
  chordEnd: null,
};

/** The document's ornament names against the element names MusicXML gives them. */
const ORNAMENT_ELEMENTS: Readonly<Record<Ornament, string>> = {
  trill: "trill-mark",
  turn: "turn",
  "inverted-turn": "inverted-turn",
  "delayed-turn": "delayed-turn",
  mordent: "mordent",
  "inverted-mordent": "inverted-mordent",
  shake: "shake",
  schleifer: "schleifer",
};

/**
 * What the `index`-th `<note>` of `event` carries.
 *
 * Everything but the arpeggio is the first note's business. An arpeggio is written on every
 * note of the chord, which is what tells a reader how far the sign reaches, and the bracket
 * form needs to say which end each note is.
 */
export function marksFor(event: MusicEvent, index: number, count: number): EventMarks {
  if (event.kind === "rest") {
    return index === 0 ? { ...NO_MARKS, fermata: event.fermata } : NO_MARKS;
  }
  // An arpeggio across one note head says nothing, so it is not written for one.
  const arpeggio = count > 1 ? event.arpeggio : null;
  const chordEnd = index === 0 ? "bottom" : index === count - 1 ? "top" : null;
  const own = index === 0;
  return {
    articulations: own ? event.articulations : [],
    ornaments: own ? event.ornaments : [],
    tremolo: own ? event.tremolo : 0,
    fermata: own ? event.fermata : null,
    arpeggio,
    chordEnd: arpeggio === "bracket" ? chordEnd : null,
  };
}

function ornamentsNode(marks: EventMarks): XmlNode | null {
  const children = marks.ornaments.map((ornament) => el(ORNAMENT_ELEMENTS[ornament]));
  // A tremolo is an ornament in MusicXML's grammar; `single` is the one written on one
  // note, as against the `start`/`stop` pair that alternates between two of them.
  if (marks.tremolo > 0) children.push(leaf("tremolo", marks.tremolo, { type: "single" }));
  return children.length === 0 ? null : el("ornaments", {}, children);
}

function arpeggioNode(marks: EventMarks): XmlNode | null {
  if (marks.arpeggio === null) return null;
  if (marks.arpeggio !== "bracket") return el("arpeggiate", { direction: marks.arpeggio });
  return marks.chordEnd === null ? null : el("non-arpeggiate", { type: marks.chordEnd });
}

/**
 * The `<notations>` element, or nothing when the note is unmarked.
 *
 * `open` is the small circle a drum left to ring carries; it is a technical mark here
 * rather than a head shape because the head is already saying which drum it is.
 */
export function notationsNode(
  pitch: NotePitch | null,
  slurs: readonly SlurMark[],
  glissandos: readonly GlissMark[],
  tuplet: TupletMark,
  marks: EventMarks,
  open: boolean,
): XmlNode | null {
  const children: (XmlNode | null)[] = [];
  if (pitch?.tieStop === true) children.push(el("tied", { type: "stop" }));
  if (pitch?.tieStart === true) children.push(el("tied", { type: "start" }));
  for (const slur of slurs) {
    children.push(el("slur", { type: slur.type, number: slur.number, placement: slur.placement }));
  }
  for (const gliss of glissandos) {
    // `line-type` is stated rather than left to the reader because the wavy line is what
    // the sign is: a straight one is a portamento and means something else.
    children.push(
      el("glissando", {
        type: gliss.type,
        number: gliss.number,
        "line-type": gliss.type === "start" ? "wavy" : null,
      }),
    );
  }
  if (tuplet !== null) children.push(el("tuplet", { type: tuplet }));
  children.push(ornamentsNode(marks));
  // A harmonic's circle is kept with the articulations and is a technical mark in the file.
  const written = marks.articulations.filter((mark) => mark !== "harmonic");
  if (written.length > 0) {
    children.push(
      el(
        "articulations",
        {},
        written.map((mark) => el(mark)),
      ),
    );
  }
  if (marks.fermata !== null) children.push(leaf("fermata", marks.fermata));
  children.push(arpeggioNode(marks));
  children.push(technicalNode(pitch, open, marks.articulations.includes("harmonic")));

  const kept = children.filter((child): child is XmlNode => child !== null);
  return kept.length === 0 ? null : el("notations", {}, kept);
}
